<template>
  <div>
    <!--      ################################ CLOSURE DIRECTION, TYPE AND SUBTYPE #####################################-->
    <v-card-title
      class="title"
      :style="{ marginTop: edit ? '0 !important' : undefined }"
    >
      {{ $t('road-maintenance/aggregator.road_closure_direction_and_type') }}
    </v-card-title>
    <div
      class="types-wrapper"
    >
      <v-row>
        <!--        Closure type-->
        <v-col
          @dblclick="$emit('set-edit', true)"
        >
          <autocomplete-field
            v-model="formDataCopy.type_id"
            :field="fieldsConfig.type"
            :disabled="!edit && !createMode"
            :options="fieldsConfig.type.autocomplete_options"
            :items="{ type_id: { data: typeOptions } }"
          />
        </v-col>

        <!--        Closure subtype-->
        <v-col
          @dblclick="$emit('set-edit', true)"
        >
          <autocomplete-field
            v-model="formDataCopy.subtype_id"
            :field="fieldsConfig.subtype"
            :options="fieldsConfig.subtype.autocomplete_options"
            :disabled="(!edit && !createMode) || disableSubtype"
            :items="{ subtype_id: { data: subtypeOptions } }"
          />
        </v-col>

        <!--          Closure start datetime-->
        <v-col
          @dblclick="$emit('set-edit', true)"
        >
          <v-menu
            v-model="openedDatePicker.start_time"
            transition="slide-y-transition"
            offset-y
            min-width="auto"
            :close-on-content-click="false"
          >
            <template
              #activator="{}"
            >
              <text-field
                v-model="formDataCopy.start_time"
                :field="fieldsConfig.start_time"
                :form-data="formDataCopy"
                :validation-errors="validationErrors"
                :disabled="!edit && !createMode"
                @append-click="openDatePicker('start_time')"
                @clear-validation-errors="$emit('clear-validation-errors', 'start_time')"
              />
            </template>
            <datetime-picker
              v-if="openedDatePicker.start_time"
              v-model="formDataCopy.start_time"
              :date-format="datetimeFormat"
              :field="fieldsConfig.start_time_date"
              @close="$set(openedDatePicker, 'start_time', false)"
              @dblclick:date="event => onDateDblClick(event)"
            />
          </v-menu>
        </v-col>

        <!--          Closure end datetime-->
        <v-col
          @dblclick="$emit('set-edit', true)"
        >
          <v-menu
            v-model="openedDatePicker.end_time"
            transition="slide-y-transition"
            offset-y
            min-width="auto"
            :close-on-content-click="false"
          >
            <template
              #activator="{}"
            >
              <text-field
                v-model="formDataCopy.end_time"
                :field="fieldsConfig.end_time"
                :form-data="formDataCopy"
                :validation-errors="validationErrors"
                :disabled="!edit && !createMode"
                @append-click="openDatePicker('end_time')"
                @clear-validation-errors="$emit('clear-validation-errors', 'end_time')"
              />
            </template>
            <datetime-picker
              v-if="openedDatePicker.end_time"
              v-model="formDataCopy.end_time"
              :date-format="datetimeFormat"
              :field="fieldsConfig.end_time_date"
              @close="$set(openedDatePicker, 'end_time', false)"
              @dblclick:date="event => onDateDblClick(event)"
            />
          </v-menu>
        </v-col>
      </v-row>
    </div>

    <!--    ##################################### ROADS REGULATION ######################################-->
    <v-card-title
      class="title"
    >
      {{ $t('road-maintenance/aggregator.roads_regulation') }}
    </v-card-title>

    <v-card
      :id="$vuetify.breakpoint.mobile ? 'card-mobile' : 'card'"
      flat
      class="d-flex flex-column flex-grow-1"
    >
      <v-card-text
        ref="tabContainer"
        :id="$vuetify.breakpoint.mobile ? 'v-card-text--mobile' : 'v-card-text'"
      >
        <!--        Tabs header-->
        <v-row
          v-if="formData && formData[dataKey] && formData[dataKey].length"
          style="overflow: auto; margin: 0;"
        >
          <v-col style="display: flex; flex-direction: column; padding: 0;">
            <v-tabs
              v-model="currentTabId"
              :class="$vuetify.breakpoint.mobile ? '' : 'tabs-border'"
              style="flex: unset;"
              :optional="!!overflowTabs.length"
              @change="onTabChange"
            >
              <v-tabs-slider
                :id="$vuetify.breakpoint.mobile ? 'tab-slider--mobile' : 'tab-slider'"
                :style="getTabSliderStyles()"
              />

              <template
                v-for="(tabItem, tabIndex) in visibleTabs"
              >
                <!-- Visible Tabs -->
                <v-tooltip
                  :key="tabIndex"
                  top
                  content-class="tab-tooltip"
                >
                  <template
                    v-slot:activator="{ on, attrs }"
                  >
                    <v-tab
                      v-bind="attrs"
                      v-on="on"
                      :ref="'tabItem-' + tabIndex"
                      :style="getVisibleTabStyles(tabIndex)"
                      :class="$vuetify.breakpoint.mobile ? 'custom-tab v-tab--mobile' : 'custom-tab'"
                      :ripple="false"
                    >
                      {{ tabItem.street
                        ? (tabItem.street.length > maximumStreetCharactersInTabName
                          ? tabItem.street.slice(0, maximumStreetCharactersInTabName) + '…'
                          : tabItem.street)
                        : $t('road-maintenance/aggregator.street') + ' ' + (tabIndex + 1)
                      }}
                    </v-tab>
                  </template>
                  <span
                  >
                    {{ tabItem.street ? tabItem.street : $t('road-maintenance/aggregator.street') + ' ' + (tabIndex + 1) }}
                  </span>
                </v-tooltip>
              </template>

              <!-- Dropdown for overflowed tabs -->
              <v-menu
                v-model="isMoreMenuOpen"
                :key="overflowMenuKey + '_overflow-menu-key'"
                offset-y
                content-class="overflow-tab-menu"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <!-- More Tabs Button -->
                  <div
                    v-if="overflowTabs.length"
                    v-on="on"
                    :style="getMoreTabStyles()"
                    class="more-tab"
                    @click="toggleMoreMenu"
                  >
                    {{ $t('base.more') }}
                    <v-icon right
                    >
                      {{ isMoreMenuOpen ? 'mdi-menu-up' : 'mdi-menu-down' }}
                    </v-icon>
                  </div>
                </template>
                <v-list>
                  <v-subheader
                    class="list-subheader"
                  >
                    {{ $t('base.choose_columns') }}
                  </v-subheader>
                  <v-list-item
                    v-for="(overflowTabItem, overflowTabIndex) in overflowTabs"
                    :key="overflowTabIndex"
                  >
                    <v-list-item-action
                      style="margin-right: 10px !important;"
                    >
                      <v-checkbox
                        :value="selectedTabIndex === overflowTabIndex"
                        color="#65AD45"
                        @change="isSelected => selectOverflowTab(isSelected, overflowTabItem, overflowTabIndex)"
                      >
                        <template
                          #label
                        >
                          <v-tooltip
                            :key="overflowTabIndex"
                            top
                            content-class="tab-tooltip"
                          >
                            <template
                              v-slot:activator="{ on, attrs }"
                            >
                              <v-list-item-title
                                v-bind="attrs"
                                v-on="on"
                                class="more-list-item"
                                :style="getMoreListItemStyle(overflowTabItem)"
                              >
                                {{ overflowTabItem.street
                                  ? (overflowTabItem.street.length > maximumStreetCharactersInTabName
                                    ? overflowTabItem.street.slice(0, maximumStreetCharactersInTabName) + '…'
                                    : overflowTabItem.street)
                                  : $t('road-maintenance/aggregator.street') + ' ' + (overflowTabIndex + 1)
                                }}
                              </v-list-item-title>
                            </template>
                            <span
                            >
                              {{ overflowTabItem.street ? overflowTabItem.street : $t('road-maintenance/aggregator.street') + ' ' + (overflowTabIndex + 1) }}
                            </span>
                          </v-tooltip>
                        </template>
                      </v-checkbox>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-tabs>
          </v-col>
        </v-row>

        <!--        Tabs content with map-->
        <v-row
          v-if="formData && formData[dataKey] && formData[dataKey].length"
          style="margin: 0; padding: 24px;"
        >
          <!--          Closure form-->
          <v-col
            style="padding: 0; min-height: 450px;"
          >
            <!-- Tabs Content -->
            <v-tabs-items
              v-model="currentTabItemId"
            >
              <v-tab-item
                v-for="(data, index) in (formDataCopy[dataKey] || [])"
                :key="index"
              >
                <v-row
                  class="possible-direction-col"
                >
                  <!--      Delete road regulation btn-->
                  <v-img
                    v-if="edit || createMode"
                    src="/img/icons/delete-file-icon.svg"
                    id="delete-file-image"
                    @click="onDeleteRoadRegulation(data)"
                  />
                  <!--              Road closure information-->
                  <v-col
                    style="padding: 0;"
                  >
                    <!--        Closure direction-->
                    <v-row>
                      <v-col
                        @dblclick="$emit('set-edit', true)"
                      >
                        <autocomplete-field
                          v-model="data.direction_id"
                          :field="fieldsConfig.direction"
                          :options="fieldsConfig.direction.autocomplete_options"
                          :disabled="!edit && !createMode"
                          :items="{ direction_id: { data: directionOptions } }"
                        />
                      </v-col>
                    </v-row>

                    <!--          Resolved street name-->
                    <v-row>
                      <v-col>
                        <text-field
                          v-model="data.street"
                          :field="fieldsConfig.street"
                          disabled
                        />
                      </v-col>
                    </v-row>

                    <!--                Description-->
                    <v-row>
                      <v-col
                        @dblclick="$emit('set-edit', true)"
                      >
                        <textarea-field
                          v-model="data.description"
                          :config="fieldsConfig.description"
                          :disabled="!edit && !createMode"
                        />
                      </v-col>
                    </v-row>

                    <!--                Lanes-->
                    <v-row
                    >
                      <v-col>
                        <div
                          v-for="(lane, laneIndex) in data.lanes"
                          :key="laneIndex"
                        >
                          <v-row
                            style="margin: 0;"
                          >
                            <!--                  Lane dropdown-->
                            <v-col
                              style="padding-left: 0;"
                              cols="10"
                            >
                              <autocomplete-field
                                v-model="lane.opened"
                                :field="{
                                  key: 'opened',
                                  label: $t('road-maintenance/aggregator.lane') + ' ' + (laneIndex + 1)
                                }"
                                :items="{ opened: { data: laneStatuses } }"
                              />
                            </v-col>
                            <!--                  Remove lane btn-->
                            <v-col
                              class="d-flex align-end"
                              cols="2"
                            >
                              <v-img
                                src="/img/icons/delete-file-icon.svg"
                                width="32"
                                height="32"
                                style="cursor: pointer; flex: unset !important;"
                                @click="$delete(data.lanes, laneIndex)"
                              />
                            </v-col>

                          </v-row>
                        </div>
                      </v-col>
                    </v-row>
                    <!--          Add new lane button-->
                    <v-row
                      style="margin-top: 0;"
                    >
                      <v-col
                        @dblclick="$emit('set-edit', true)"
                      >
                        <universal-button
                          type="new"
                          :label="$t('road-maintenance/aggregator.add_lane')"
                          :disabled="!edit && !createMode"
                          @click="addLane(index)"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
          <!--              Map-->
          <v-col
            style="padding: 0; min-height: 450px;"
          >
            <div
              ref="map_note"
              class="map_note"
            >
              {{ $t('road-maintenance/aggregator.map_note') }}
            </div>

            <!--    Map info popup-->
            <div
              ref="map_info"
              class="map_info"
              @click="showMapInfoDialog = true"
            >
              ?
            </div>
            <google-map
              :unique-id="'road-regulation-map'"
              :config="mapConfig"
              @map:objects="event => onMapObjectsUpdate(event)"
              @map:click="onMapClick"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <div
      @dblclick="$emit('set-edit', true)"
    >
      <universal-button
        type="new"
        style="margin: 0 0 24px 24px;"
        :disabled="!edit && !createMode"
        :label="$t('road-maintenance/aggregator.add_new_road')"
        @click="addRoadRegulation"
      />
    </div>

    <draw-road-regulation-on-map-dialog
      v-if="showDrawRoadRegulationDialog"
      :show-dialog="showDrawRoadRegulationDialog"
      @close="showDrawRoadRegulationDialog = false"
      @drawn-polyline="onDrawnPolyline"
    />

    <map-info
      :show-dialog="showMapInfoDialog"
      @close="showMapInfoDialog = false"
    >
      ?
    </map-info>
  </div>
</template>

<script>
import AutocompleteField from '@/global/components/view-layouts/tabs-layout/components/fields/AutocompleteField.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import GoogleMap from '@/global/components/google-map/components/GoogleMap.vue'
import DrawRoadRegulationOnMapDialog from './DrawRoadRegulationOnMapDialog.vue'
import TextareaField from '@/global/components/view-layouts/tabs-layout/components/fields/TextareaField.vue'
import TextField from '@/global/components/view-layouts/tabs-layout/components/fields/TextField.vue'
import MapInfo from './MapInfo.vue'
import DatetimePicker from '@/global/components/view-layouts/tabs-layout/components/fields/DatetimePicker.vue'
import { validateDateTimeFormat } from '@/global/services/helpers/dates'
import { api } from '@/global/services/api'

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm'
const VIEW_DATETIME_FORMAT = 'DD.MM.YYYY. HH:mm'
const DEFAULT_TAB_WIDTH_DESKTOP = 223
const DEFAULT_TAB_WIDTH_MOBILE = 107
const MAXIMUM_STREET_CHARACTERS_IN_TAB_NAME = 20
const HIGHLIGHTING_TAB_TIMEOUT_IN_MS = 500
export default {
  name: 'DrawRoadRegulation',

  components: {
    DatetimePicker,
    MapInfo,
    TextField,
    TextareaField,
    DrawRoadRegulationOnMapDialog,
    GoogleMap,
    UniversalButton,
    AutocompleteField
  },

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    updateValidationErrors: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      formDataCopy: {},
      showMapInfoDialog: false,
      showDrawRoadRegulationDialog: false,
      directionOptions: [],
      laneStatuses: [
        {
          id: true,
          name: this.$t('road-maintenance/aggregator.lane_opened_status')
        },
        {
          id: false,
          name: this.$t('road-maintenance/aggregator.lane_closed_status')
        }
      ],
      typeOptions: [],
      subtypeOptions: [],
      openedDatePicker: {},
      datetimeFormat: DATETIME_FORMAT,
      mapObjectsToManipulate: [],
      mapConfig: {
        disablePoiClicks: true,
        style: {
          borderRadius: '16px'
        },
        zoom: 10
      },
      currentTabId: 0,
      currentTabItemId: 0,
      visibleTabs: [],
      overflowTabs: [],
      isMoreMenuOpen: false,
      isSelectedMoreTab: false,
      overflowMenuKey: 0,
      selectedTabIndex: null,
      totalTabWidth: 0,
      tabContainerWidth: 0,
      resizeTimeoutId: null,
      tabWidthExceedsValue: 0,
      map: null,
      googleMaps: null,
      existingRoadRegulation: null,
      disableSubtype: false,
      maximumStreetCharactersInTabName: MAXIMUM_STREET_CHARACTERS_IN_TAB_NAME,
      tabHighlights: {},
      highlightTimeouts: {},
      highlightOverflowTab: false,
      overflowHighlightTimeoutId: null
    }
  },

  computed: {
    dataKey () {
      return this.config?.dataKey || null
    },

    fieldsConfig () {
      return {
        type: {
          key: 'type_id',
          label: this.$t('road-maintenance/aggregator.closure_type'),
          autocomplete_options: {
            custom_display_value: (val) => {
              return val && val.description ? this.$t(val.description) : null
            }
          },
          editable: true,
          creatable: true,
          visible: true
        },
        subtype: {
          key: 'subtype_id',
          label: this.$t('road-maintenance/aggregator.closure_subtype'),
          autocomplete_options: {
            custom_display_value: (val) => {
              return val && val.description ? this.$t(val.description) : null
            }
          },
          editable: true,
          creatable: true,
          visible: true
        },
        start_time: {
          key: 'start_time',
          appendIcon: 'mdi-calendar-month',
          label: this.$t('road-maintenance/aggregator.road_closure_start_time'),
          placeholder: VIEW_DATETIME_FORMAT,
          onInput: (value) => {
            if (value && typeof this.updateValidationErrors === 'function' && !validateDateTimeFormat(value, VIEW_DATETIME_FORMAT)) {
              this.updateValidationErrors({
                start_time: this.$t('base.invalid_date').replace(':format', VIEW_DATETIME_FORMAT)
              })
            }
          },
          visible: true,
          creatable: true,
          editable: true
        },
        start_time_date: {
          key: 'start_time',
          format: VIEW_DATETIME_FORMAT,
          placeholder: VIEW_DATETIME_FORMAT
        },
        end_time: {
          key: 'end_time',
          appendIcon: 'mdi-calendar-month',
          label: this.$t('road-maintenance/aggregator.road_closure_end_time'),
          placeholder: VIEW_DATETIME_FORMAT,
          onInput: (value) => {
            if (value && typeof this.updateValidationErrors === 'function' && !validateDateTimeFormat(value, VIEW_DATETIME_FORMAT)) {
              this.updateValidationErrors({
                end_time: this.$t('base.invalid_date').replace(':format', VIEW_DATETIME_FORMAT)
              })
            }
          },
          visible: true,
          creatable: true,
          editable: true
        },
        end_time_date: {
          key: 'end_time',
          format: VIEW_DATETIME_FORMAT,
          placeholder: VIEW_DATETIME_FORMAT
        },
        direction: {
          key: 'direction_id',
          label: this.$t('road-maintenance/aggregator.select_direction'),
          autocomplete_options: {
            custom_display_value: (val) => {
              return val && val.description ? this.$t(val.description) : null
            }
          },
          editable: true,
          creatable: true,
          visible: true
        },
        street: {
          key: 'street',
          label: this.$t('road-maintenance/aggregator.street'),
          editable: false,
          creatable: false,
          visible: true
        },
        description: {
          key: 'description',
          label: this.$t('road-maintenance/aggregator.closure_description'),
          editable: true,
          creatable: true,
          visible: true
        }
      }
    }
  },

  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler (val) {
        this.formDataCopy = val
        if (!this.dataKey || !val[this.dataKey]) {
          this.$set(this.formDataCopy, this.dataKey, [{
            id: this.generateUniqueId()
          }])
        }
      }
    },

    'formData.type_id': {
      immediate: true,
      deep: true,
      handler (val) {
        // Map closure subtypes to the correspondent closure type
        this.mapSubtypeOptionsByType()
      }
    },
    edit: {
      immediate: true,
      handler (val) {
        if (this.map && this.googleMaps) {
          if (val) {
            this.showMapObjects()
          }
          else {
            this.hideMapObjects()
            this.adjustTabs(false)
          }
        }
      }
    }
  },

  mounted () {
    this.adjustTabs()
    window.addEventListener('resize', this.adjustTabs)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.adjustTabs)
  },

  async created () {
    try {
      const { data } = await api('road-maintenance').get('road-activity-feed/attributes')
      const { types, directions } = data || {}

      if (types && types.length) {
        this.typeOptions = types
        this.mapSubtypeOptionsByType()
      }

      if (directions && directions.length) {
        this.directionOptions = directions
      }
    }
    catch (exception) {
      console.error(exception)
    }
  },

  methods: {
    mapSubtypeOptionsByType () {
      const { type_id: typeId } = this.formData || {}

      if (typeId) {
        const foundType = this.typeOptions.find(type => type.id === typeId)

        this.disableSubtype = !foundType || !foundType.subtypes || !foundType.subtypes.length
        if (foundType && foundType.subtypes) {
          this.subtypeOptions = foundType.subtypes
        }
      }
    },

    openDatePicker (fieldKey) {
      if (!fieldKey) {
        return
      }

      this.$set(this.openedDatePicker, fieldKey, true)
    },

    onDateDblClick (event) {
      const { applyDate } = event || {}

      if (applyDate && typeof applyDate === 'function') {
        applyDate()
      }
    },

    // Generate unique id for each road regulation
    generateUniqueId () {
      return Date.now().toString(36) + Math.random().toString(36).substring(2, 12).padStart(12)
    },

    addRoadRegulation () {
      this.formDataCopy[this.dataKey].push({
        id: this.generateUniqueId()
      })
      this.adjustTabs(false)
      this.highlightTab(this.formDataCopy[this.dataKey].length - 1)
    },

    onDeleteRoadRegulation (data) {
      const { id } = data || {}
      const index = this.formDataCopy[this.dataKey].findIndex(item => item.id === id)

      if (index !== -1) {
        // Remove item
        this.formDataCopy[this.dataKey].splice(index, 1)
        // Re-index array to be able to draw polyline from data that comes at removed index after removing it
        this.formDataCopy[this.dataKey] = [...this.formDataCopy[this.dataKey]]

        const nextItem = this.formDataCopy[this.dataKey][index] || this.formDataCopy[this.dataKey][index - 1] || null

        // Handle current tab id after re-indexing array
        // currentTabItemId will be used in the onDrawnPolyline method as an index
        if (this.currentTabItemId > this.formDataCopy[this.dataKey].length - 1) {
          this.currentTabItemId = this.formDataCopy[this.dataKey].length - 1
          this.currentTabId = this.formDataCopy[this.dataKey].length - 1
          this.isSelectedMoreTab = false
          this.selectedTabIndex = null
        }

        this.isMoreMenuOpen = false
        // Draw new data polyline after reindexing
        this.onDrawnPolyline(nextItem)

        this.adjustTabs(false)
      }
    },

    addLane (index) {
      // Make sure the lanes array exists
      if (!Array.isArray(this.formDataCopy[this.dataKey][index].lanes)) {
        this.$set(this.formDataCopy[this.dataKey][index], 'lanes', [])
      }

      // Now push a new lane into the lanes array
      this.formDataCopy[this.dataKey][index].lanes.push({})
    },

    onMapObjectsUpdate (event) {
      const { map, googleMaps } = event || {}
      if (!map || !googleMaps) return
      this.map = map
      this.googleMaps = googleMaps

      const controlDivRef = this.$refs.map_note
      const mapInfoRef = this.$refs.map_info

      const tryAddControl = (refEl, position, display) => {
        const el = Array.isArray(refEl) ? refEl[0] : refEl
        if (!el) return

        el.style.display = display
        map.controls[googleMaps.ControlPosition[position]]?.push(el)

        this.mapObjectsToManipulate.push({
          show: () => {
            if (el?.style) el.style.display = display
          },
          hide: () => {
            if (el?.style) el.style.display = 'none'
          }
        })
      }

      tryAddControl(controlDivRef, 'LEFT_TOP', 'block')
      tryAddControl(mapInfoRef, 'RIGHT_TOP', 'flex')

      if (!this.edit && !this.createMode) {
        this.hideMapObjects()
        return
      }

      this.showMapObjects()
    },

    hideMapObjects () {
      this.mapObjectsToManipulate.forEach(obj => {
        obj.hide()
      })
    },

    showMapObjects () {
      this.mapObjectsToManipulate.forEach(obj => {
        obj.show()
      })
    },

    onMapClick () {
      this.showDrawRoadRegulationDialog = this.edit || this.createMode
    },

    // Event occurred when a user finishes drawing a road
    onDrawnPolyline (event) {
      const { polyline, street } = event || {}

      if (this.map && this.googleMaps) {
        // Set address street (resolved from backend)
        if (this.formDataCopy[this.dataKey] && this.formDataCopy[this.dataKey][this.currentTabItemId]) {
          this.$set(this.formDataCopy[this.dataKey][this.currentTabItemId], 'street', street)
        }

        // Remove a previously added polyline
        if (this.existingRoadRegulation) {
          this.existingRoadRegulation.setMap(null)
          this.existingRoadRegulation = null
        }

        if (polyline) {
          // Show drawn polyline on a side map as a preview
          this.existingRoadRegulation = new this.googleMaps.Polyline({
            path: polyline,
            geodesic: true,
            strokeColor: 'black',
            strokeOpacity: 1.0,
            strokeWeight: 2
          })

          this.existingRoadRegulation.setMap(this.map)

          // Set an arrow between each 2 points to show a road direction
          const icons = []
          for (let i = 0; i < polyline.length - 1; i++) {
            const offset = ((i + 0.5) / (polyline.length - 1)) * 100
            icons.push({
              icon: {
                path: this.googleMaps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 2,
                strokeColor: 'red'
              },
              offset: `${offset}%`
            })
          }

          this.existingRoadRegulation.setOptions({
            icons
          })

          // Set polyline to form data
          this.$set(this.formDataCopy[this.dataKey][this.currentTabItemId], 'polyline', polyline)
          // Fit map bounds to the polyline
          const bounds = new this.googleMaps.LatLngBounds()
          polyline.forEach(coordinate => bounds.extend(coordinate))
          this.map.fitBounds(bounds)
        }
        // Reset map zoom if there is no a polyline to display
        else if (this.mapConfig?.zoom) {
          this.map.setZoom(this.mapConfig.zoom)
        }
      }
    },

    // ############ Methods for tabs ################################
    adjustTabs (useTimeout = true) {
      // Timeout here is for optimization. Calculate tabs dimensions one time, when resize is finished.
      if (this.resizeTimeoutId) {
        clearTimeout(this.resizeTimeoutId)
      }

      const tryToAdjust = () => {
        this.tabContainerWidth = this.$refs.tabContainer ? this.$refs.tabContainer.offsetWidth : 0
        this.visibleTabs = []
        this.overflowTabs = []
        this.totalTabWidth = 0

        const tabWidth = this.$vuetify.breakpoint.mobile ? DEFAULT_TAB_WIDTH_MOBILE : DEFAULT_TAB_WIDTH_DESKTOP

        this.formDataCopy[this.dataKey].forEach(tab => {
          this.totalTabWidth += tabWidth

          if (this.totalTabWidth + tabWidth <= this.tabContainerWidth) {
            this.visibleTabs.push(tab)
          }
          else {
            if (!this.tabWidthExceedsValue) {
              this.tabWidthExceedsValue = this.tabContainerWidth - this.totalTabWidth
            }
            this.overflowTabs.push(tab)
          }
        })

        if (this.overflowTabs.length) {
          this.overflowMenuKey++
        }
      }

      if (useTimeout) {
        this.resizeTimeoutId = setTimeout(() => {
          tryToAdjust()
        }, 200)
      }
      else {
        tryToAdjust()
      }
    },

    toggleMoreMenu () {
      this.isMoreMenuOpen = !this.isMoreMenuOpen
    },

    selectOverflowTab (isSelected, tabItem, tabIndex) {
      this.selectedTabIndex = isSelected ? tabIndex : null
      this.isSelectedMoreTab = isSelected

      if (isSelected && this.formData[this.dataKey]) {
        this.currentTabItemId = this.formData[this.dataKey].findIndex((item) => {
          return (tabItem.id === item.id)
        })
        this.currentTabId = null

        if (this.formDataCopy[this.dataKey] && this.formDataCopy[this.dataKey][this.currentTabItemId]) {
          this.onDrawnPolyline(this.formDataCopy[this.dataKey][this.currentTabItemId])
        }
      }
      else {
        this.currentTabId = 0
        this.currentTabItemId = this.currentTabId
      }
    },

    onTabChange (index) {
      if (this.currentTabId !== undefined) {
        this.currentTabItemId = this.currentTabId
      }
      if (this.isSelectedMoreTab) {
        this.isSelectedMoreTab = false
        this.selectedTabIndex = null
      }

      if (this.formDataCopy[this.dataKey] && this.formDataCopy[this.dataKey][index]) {
        this.onDrawnPolyline(this.formDataCopy[this.dataKey][index])
      }
    },

    highlightTab (tabIndex) {
      if (!this.overflowTabs.length) {
        this.$set(this.tabHighlights, tabIndex, true)

        if (this.highlightTimeouts[tabIndex]) {
          clearTimeout(this.highlightTimeouts[tabIndex])
        }

        this.highlightTimeouts[tabIndex] = setTimeout(() => {
          this.$delete(this.tabHighlights, tabIndex)
          delete this.highlightTimeouts[tabIndex]
        }, HIGHLIGHTING_TAB_TIMEOUT_IN_MS)
      }
      else {
        this.highlightOverflowTab = true
        clearTimeout(this.overflowHighlightTimeoutId)

        this.overflowHighlightTimeoutId = setTimeout(() => {
          this.highlightOverflowTab = false
        }, HIGHLIGHTING_TAB_TIMEOUT_IN_MS)
      }
    },

    getVisibleTabStyles (tabIndex) {
      const tabWidth = this.$vuetify.breakpoint.mobile ? DEFAULT_TAB_WIDTH_MOBILE : DEFAULT_TAB_WIDTH_DESKTOP
      const style = {
        maxWidth: 'unset',
        width: tabWidth + 'px'
      }

      if (this.tabHighlights[tabIndex]) {
        style.backgroundColor = 'rgb(101, 173, 69)'
      }

      return style
    },

    getTabSliderStyles () {
      const tabWidth = this.$vuetify.breakpoint.mobile ? DEFAULT_TAB_WIDTH_MOBILE : DEFAULT_TAB_WIDTH_DESKTOP

      const style = {
        maxWidth: 'unset',
        width: tabWidth + 'px'
      }

      if (this.isSelectedMoreTab) {
        style.height = '0px !important'
        style.border = 'unset !important'
      }

      return style
    },

    getMoreTabStyles () {
      const tabWidth = this.$vuetify.breakpoint.mobile ? DEFAULT_TAB_WIDTH_MOBILE : DEFAULT_TAB_WIDTH_DESKTOP

      const style = {
        maxWidth: 'unset',
        width: tabWidth + 'px',
        padding: 'unset'
      }

      if (this.isSelectedMoreTab) {
        style.borderBottom = '2px solid #65AD45'
        style.color = '#111827'
      }
      else {
        style.borderBottom = 'unset'
      }

      if (this.highlightOverflowTab) {
        style.backgroundColor = 'rgb(101, 173, 69)'
      }

      return style
    },

    getMoreListItemStyle (tabItem) {
      const style = {}

      if (tabItem.disabled) {
        style.color = 'rgba(100,98,98,0.54)'
      }

      return style
    }
  }
}
</script>

<style scoped lang="scss">
.title {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  line-height: 28px !important;
  color: #111827 !important;
  margin-left: 10px !important;
  margin-top: 20px !important;
}

.wrapper {
  position: relative;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  padding: 24px;
  margin: 0 24px;
}

.types-wrapper {
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  padding: 24px;
  margin: 0 24px 24px 24px;
}

.with-margin {
  margin-top: 24px;
}

.direction-col {
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 24px;
}

.possible-direction-col {
  position: relative;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 24px;
  margin: 0 24px 0 0;
}

.second-direction-title {
  margin-top: 24px !important;
}

.direction-row {
  margin-top: 24px;
}

.direction-title {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #111827 !important;
  margin: 0 0 5px 5px;
}

#delete-file-image {
  position: absolute;
  width: 32px;
  height: 32px;
  top: 3px;
  right: 8px;
  cursor: pointer;
}

.possible-direction-title {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.map_note {
  padding: 10px 16px 10px 16px;
  border-radius: 8px;
  margin: 8px;
  background-color: #65AD45;
  display: none;
  color: white;
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  max-width: 80%;
}

.map_info {
  position: relative;
  display: none;
  align-items: center;
  justify-content: center;
  margin: 8px;
  width: 40px;
  height: 40px;
  background-color: #FFFFFF;
  border-radius: 50%;
  border: 1px solid #E5E7EB;
  font-size: 20px;
  font-weight: bold;
  color: #000000;
  cursor: pointer;
}

#v-card-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 0;
}

.tabs-border {
  border-bottom: #E5E7EB 1px solid;
  border-top: #E5E7EB 1px solid;
}

#tab-slider {
  border: #65AD45 3px solid;
  width: 223px;
}

#tab-slider--mobile {
  border: #65AD45 3px solid;
  width: 107px;
}

::v-deep {
  .custom-tab .v-tab--active {
    background: unset !important;
  }
  .custom-tab .v-tab:before {
    background-color: unset !important;
  }
  .custom-tab .v-tab {
    color: #6B7280;
    font-weight: 500;
    font-family: 'Satoshi', sans-serif;
    font-size: 14px;
    width: 223px;
    text-transform: none !important;
  }

  .custom-tab .v-tab--mobile {
    color: #6B7280;
    font-weight: 500;
    font-size: 12px;
    width: 107px;
  }
  .custom-tab .v-tab--active {
    color: #111827;
  }

  .v-slide-group__prev {
    display: none !important;
  }

  .v-slide-group__next {
    display: none !important;
  }

  .v-slide-groupd__wrapper {
    display: block;
  }
}

.more-tab {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 500;
  font-family: 'Satoshi', sans-serif;
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 0 1 auto;
  justify-content: center;
  letter-spacing: 0.0892857143em;
  line-height: normal;
  min-width: 90px;
  max-width: 360px;
  outline: none;
  padding: 0 16px;
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.more-list-item {
  color: #111827;
  font-weight: 500;
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  margin-left: 10px;
}

.overflow-tab-menu {
  border-radius: 16px;
  max-height: 60vh;
}

.list-subheader {
  font-family: 'Satoshi', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #111827;
  border-bottom: 1px solid #E5E7EB;
}

.tab-tooltip {
  opacity: 1 !important;
  background-color: rgba(#a4a0a0, 1) !important;
  transition: background-color 0.3s ease;
}
</style>
